import { useState } from "react"
import {
  ASK_DYNAMO_THOUGHT_FREQUENCIES,
  type AskDynamoThoughtFrequency,
  thoughtFrequencyLabel,
  thoughtFrequencyScheduleCopy,
} from "~/data/askDynamoThoughtFrequencies"
import { updateProfile } from "~/api/auth"

interface AskDynamoThoughtsPreferenceCardProps {
  initialFrequency: AskDynamoThoughtFrequency
  onboardingCompleted: boolean
  onSaved: (frequency: AskDynamoThoughtFrequency, onboardingCompleted: boolean) => void
}

export function AskDynamoThoughtsPreferenceCard({
  initialFrequency,
  onboardingCompleted,
  onSaved,
}: AskDynamoThoughtsPreferenceCardProps) {
  const [expanded, setExpanded] = useState(!onboardingCompleted)
  const [frequency, setFrequency] = useState<AskDynamoThoughtFrequency>(initialFrequency)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      await updateProfile({
        ask_dynamo_thought_frequency: frequency,
        ask_dynamo_thought_onboarding_completed: true,
      })
      onSaved(frequency, true)
      setExpanded(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save preference")
    } finally {
      setSaving(false)
    }
  }

  if (!expanded) {
    return (
      <section
        className="mt-4 rounded-xl border border-[var(--color-ask-dynamo)]/20 bg-[var(--color-ask-dynamo-surface)]/60 px-4 py-3 text-sm"
        aria-label="Ask Dynamo Thoughts preference"
      >
        <p className="font-medium text-[var(--color-text-primary)]">
          Ask Dynamo Thoughts: {thoughtFrequencyLabel(frequency)}
        </p>
        <p className="mt-1 text-[var(--color-text-secondary)]">
          {thoughtFrequencyScheduleCopy(frequency)}
        </p>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-sm font-medium text-[var(--color-ask-dynamo-muted)] underline"
        >
          Change
        </button>
      </section>
    )
  }

  return (
    <section
      className="mt-4 rounded-2xl border border-[var(--color-ask-dynamo)]/20 bg-gradient-to-br from-[var(--color-ask-dynamo-surface)] to-white/80 p-4 shadow-[0_8px_32px_rgba(108,61,242,0.12)]"
      aria-labelledby="ask-dynamo-thoughts-heading"
    >
      <h3
        id="ask-dynamo-thoughts-heading"
        className="text-base font-semibold text-[var(--color-text-primary)]"
      >
        Never run out of things to ask
      </h3>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        Get Ask Dynamo Thoughts delivered straight to your inbox.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {ASK_DYNAMO_THOUGHT_FREQUENCIES.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setFrequency(value)}
            aria-pressed={frequency === value}
            className={`rounded-lg border px-3 py-2 text-left text-sm font-medium transition ${
              frequency === value
                ? "border-[var(--color-ask-dynamo)] bg-[var(--color-ask-dynamo)] text-white"
                : "border-[var(--color-divider)] bg-white/70 text-[var(--color-text-primary)]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={() => void handleSave()}
        className="mt-3 w-full rounded-lg bg-[var(--color-ask-dynamo)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Preference"}
      </button>
    </section>
  )
}
